import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {getHighImpactChangelogFromChangelog} from 'config-migration/import/HighImpactChanges';
import {
  CHANGELOG_VERB_PAST_TENSE,
  ChangelogVerb,
  DIFF_TYPE_TO_VERB_PRESENT,
  DIFF_TYPE_TO_VERB_PAST,
  MigrationChangelogRenderType,
  MigrationChangeType,
  MigrationDiff,
  MigrationObjectChange,
  PathSegment,
  SegmentType,
  CHANGELOG_VERB_PRESENT_TENSE,
} from 'config-migration/import/import-changelog/MigrationChangelogConstants';
import {generateSections, ObjectSectionConfig} from 'config-migration/import/ObjectSectionConfig';
import {LogLevel} from 'config-migration/migration-log/LogLevel';
import LogSection, {
  getSectionNameForObjectType,
  LogMessageGroup,
} from 'config-migration/migration-log/LogSection';
import {MigrationError} from 'config-migration/MigrationConstants';
import {OBJECT_TYPE_TO_DISPLAY_TEXT, ObjectType} from 'config-migration/types/ObjectType';
import getTestProps from 'test/lib/cypress/getTestProps';
import FieldLabel from 'ui-library/inputs/FieldLabel';

import {LogDetail, LogMessageData, makeSimpleLogMessage} from '../../migration-log/Log';
import {getHighImpactChangeFromMigrationDiff} from '../HighImpactChanges';

export interface ImportChangelogProps {
  futureTense: boolean;
  changelog: Array<MigrationObjectChange>;
  errorsByObjectId: {[objectId: string]: Array<MigrationError>};
  showObjectUrl: boolean;
}

export interface ImportChangelogSectionProps {
  futureTense: boolean;
  /** Nested to support log groups in a changelog, ie. Templates grouped by their TemplateCollection */
  changes: Array<Array<MigrationObjectChange>>;
  errorsByObjectId: {[objectId: string]: Array<MigrationError>};
  sectionDisplayName: string;
  showObjectUrl: boolean;
}

interface ImportDiffDescriptionProps {
  diff: MigrationDiff;
  futureTense: boolean;
  renderType: MigrationChangelogRenderType;
  parentObjectDisplayName: string;
}

function applyStylingToDiffPath(
  diffPath: Array<PathSegment>,
  parentObjectDisplayName: string
): Array<string | JSX.Element> {
  // We fall back to the parent name if path is null
  if (!diffPath.length) {
    return [<strong key={0}>{parentObjectDisplayName}</strong>];
  }

  // First we apply bold styling to the labeled path segments
  const boldedPathSegments = diffPath.map((segment, index) =>
    segment.segmentType !== SegmentType.FIELD_LABEL ? (
      <strong key={index}>{segment.segmentName}</strong>
    ) : (
      segment.segmentName
    )
  );

  // Next we join the path segments with space
  const spaceSeparatedPathSegments = boldedPathSegments.flatMap((segment, index, path) =>
    path.length - 1 !== index ? [segment, ' '] : segment
  );

  return spaceSeparatedPathSegments;
}

export function ImportDiffDescription(props: ImportDiffDescriptionProps): JSX.Element {
  const {diff, futureTense, renderType, parentObjectDisplayName} = props;
  const verb = futureTense
    ? CHANGELOG_VERB_PRESENT_TENSE[diff.changelogVerb]
    : CHANGELOG_VERB_PAST_TENSE[diff.changelogVerb];
  const path = applyStylingToDiffPath(diff.diffPath.slice(0, -1), parentObjectDisplayName);
  const fieldName = diff.diffPath.length ? diff.diffPath[diff.diffPath.length - 1].segmentName : '';
  if (diff.diffType === MigrationChangeType.UPDATE) {
    if (diff.changelogVerb === ChangelogVerb.ARCHIVE || diff.changelogVerb === ChangelogVerb.UNARCHIVE) {
      return (
        <span>
          {verb} {path}
        </span>
      );
    }
    if (diff.changelogVerb === ChangelogVerb.RENAME) {
      return (
        <span>
          {verb} {path} to <strong>{diff.rightValue}</strong>
        </span>
      );
    }
    if (diff.changelogVerb === ChangelogVerb.MARK) {
      return (
        <span>
          {verb} {path}: {fieldName} as <strong>{diff.rightValue}</strong>
        </span>
      );
    }
    if (renderType === MigrationChangelogRenderType.MASK_DETAILS) {
      return (
        <span>
          {verb} {path}: {fieldName} changed
        </span>
      );
    } else {
      return (
        <span>
          {verb} {path}: {fieldName} changed from <strong>{diff.leftValue || 'None'}</strong> to{' '}
          <strong>{diff.rightValue || 'None'}</strong>
        </span>
      );
    }
  } else {
    const className = diff.objectClass;
    return (
      <span>
        {verb} {className} {diff.objectDisplayName && <strong>{diff.objectDisplayName}</strong>}
      </span>
    );
  }
}

export function convertChangeToLogMessage(
  change: MigrationObjectChange,
  futureTense: boolean,
  errors: Array<MigrationError>,
  url?: string | null
): LogMessageData {
  const verb = futureTense
    ? DIFF_TYPE_TO_VERB_PRESENT[change.objectAction]
    : DIFF_TYPE_TO_VERB_PAST[change.objectAction];

  const diffDetails: Array<LogDetail> = change.diffDetails.map((difference) => {
    const detail: LogDetail = {
      message: (
        <ImportDiffDescription
          diff={difference}
          futureTense={futureTense}
          renderType={difference.renderType}
          parentObjectDisplayName={change.objectDisplayName}
        />
      ),
      level: LogLevel.INFO,
    };
    const highImpactChange = getHighImpactChangeFromMigrationDiff(difference, change);
    if (highImpactChange) {
      detail.highImpactChange = highImpactChange;
    }
    return detail;
  });

  const errorDetails: Array<LogDetail> = errors.map((error) => ({
    message: error.message,
    level: error.level,
  }));
  const displayNameElement = <strong>{change.objectDisplayName}</strong>;

  return {
    message: (
      <span>
        {verb} {OBJECT_TYPE_TO_DISPLAY_TEXT[change.objectType].toLowerCase()}{' '}
        {url ? (
          <a href={url} target="_blank" rel="noopener noreferrer">
            {displayNameElement}
          </a>
        ) : (
          displayNameElement
        )}
      </span>
    ),
    details: diffDetails.concat(errorDetails),
  };
}

/** Filter out MigrationObjectChanges that don't include a change or their object has an error */
export function filterChanges(
  changes: Array<MigrationObjectChange>,
  errorsByObjectId: {[objectId: string]: Array<MigrationError>}
): Array<MigrationObjectChange> {
  return changes.filter(
    (objectDiff) =>
      objectDiff.objectAction !== MigrationChangeType.NO_CHANGE || errorsByObjectId[objectDiff.objectId]
  );
}

export function ImportChangelogSection(props: ImportChangelogSectionProps): JSX.Element {
  const logMessageGroups: Array<LogMessageGroup> = [];
  props.changes.forEach((changeGroup) => {
    const filteredChanges = filterChanges(changeGroup, props.errorsByObjectId);
    const logMessages = filteredChanges.map((change) =>
      convertChangeToLogMessage(change, props.futureTense, props.errorsByObjectId[change.objectId] || [])
    );
    if (logMessages.length === 0) {
      logMessages.push(makeSimpleLogMessage(`No changes to ${props.sectionDisplayName.toLowerCase()}`));
    }
    logMessageGroups.push(logMessages);
  });
  return <LogSection sectionDisplayName={props.sectionDisplayName} logMessageGroups={logMessageGroups} />;
}

function getChangelogSectionKey(change: MigrationObjectChange): string {
  if (change.objectType === 'Template') {
    if (change.objectAttributes.hasOwnProperty('processTaskSchemaId')) {
      return getSectionNameForObjectType(ObjectType.ProcessTaskSchema);
    } else {
      return getSectionNameForObjectType(ObjectType.Template);
    }
  }
  return getSectionNameForObjectType(change.objectType);
}

function updateChangelogGroupsForSection(
  section: {
    objects: Array<MigrationObjectChange>;
    config: ObjectSectionConfig;
  },
  sectionToChangelogGroups: Map<string, Array<Array<MigrationObjectChange>>>,
  sectionConfigMap: Map<string, ObjectSectionConfig>
): void {
  const sectionKey: string = getChangelogSectionKey(section.objects[0]);
  const existingGroup: Array<Array<MigrationObjectChange>> = sectionToChangelogGroups.get(sectionKey) || [];
  if (existingGroup.length === 0) {
    sectionConfigMap.set(sectionKey, section.config);
  }
  existingGroup.push(section.objects);
  sectionToChangelogGroups.set(sectionKey, existingGroup);
}

function getChangelogComponents(
  sectionToChangelogGroups: Map<string, Array<Array<MigrationObjectChange>>>,
  sectionConfigMap: Map<string, ObjectSectionConfig>,
  props: ImportChangelogProps
): Array<JSX.Element> {
  const changelogGroups: Array<JSX.Element> = [];
  sectionToChangelogGroups.forEach((groups, groupKey) => {
    const config = sectionConfigMap.get(groupKey);
    if (config) {
      changelogGroups.push(
        <config.changelogComponent
          key={groupKey}
          futureTense={props.futureTense}
          changes={groups}
          errorsByObjectId={props.errorsByObjectId}
          sectionDisplayName={config.displayNameFn(groups[0])}
          showObjectUrl={props.showObjectUrl}
        />
      );
    }
  });
  return changelogGroups;
}

export function ImportChangelogSectionGroup(props: ImportChangelogProps): JSX.Element {
  // Each changelog section has a section header, with groups of logs separated by a line break.
  // Because generateSections only creates groups, we need to join these groups into sections (ie. Templates)
  const sections = generateSections(props.changelog);
  const sectionConfigMap: Map<string, ObjectSectionConfig> = new Map();
  const sectionToChangelogGroups: Map<string, Array<Array<MigrationObjectChange>>> = new Map();

  sections.forEach((section) => {
    updateChangelogGroupsForSection(section, sectionToChangelogGroups, sectionConfigMap);
  });

  const changelogGroups: Array<JSX.Element> = getChangelogComponents(
    sectionToChangelogGroups,
    sectionConfigMap,
    props
  );

  return <>{changelogGroups}</>;
}

export default function ImportChangelog(props: ImportChangelogProps): JSX.Element {
  const label = props.futureTense ? 'Review & Apply Migration' : 'Changelog';
  const highImpactChangelog = getHighImpactChangelogFromChangelog(props.changelog);
  let highImpactHighlights;
  if (highImpactChangelog.length !== 0) {
    const highImpactProps = {...props, changelog: highImpactChangelog};
    highImpactHighlights = <ImportChangelogSectionGroup {...highImpactProps} />;
  } else {
    highImpactHighlights = <p>There are no changes flagged as high impact.</p>;
  }

  return (
    <div className="u-spacing-top" {...getTestProps({component: 'ImportChangelog', element: 'div'})}>
      <>
        <FieldLabel label={label} className={css(styles.topLabel)} />
        <br />
      </>
      <>
        <FieldLabel label="High Impact Change Highlights" className={css(styles.groupLabel)} />
        {highImpactHighlights}
      </>
      <>
        <FieldLabel label="All Changes" className={css(styles.groupLabel)} />
        <ImportChangelogSectionGroup {...props} />
      </>
    </div>
  );
}

const styles = StyleSheet.create({
  groupLabel: {
    fontSize: 14,
  },
  topLabel: {
    fontSize: 16,
  },
});
